import type { Query, QueryClient } from '@tanstack/react-query';
import type { PersistQueryClientOptions } from '@tanstack/query-persist-client-core';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QUERY_PERSIST_MAX_AGE_MS } from './queryPersistConstants';

/**
 * ## What gets persisted
 *
 * Successful TanStack Query cache entries whose key’s first segment is one of
 * {@link PERSISTED_QUERY_FIRST_SEGMENTS} are dehydrated to **localStorage** under
 * {@link QUERY_PERSIST_STORAGE_KEY}.
 *
 * ## New tab / cold start
 *
 * {@link bootstrapPersistedQueryClient} runs **before** `createRoot().render()` and calls
 * `persistQueryClientRestore`, so the **same** `QueryClient` passed into {@link QueryProvider}
 * already contains hydrated queries on the first paint (instant cache for aggregation, customer
 * and recently-played when present on disk).
 *
 * ## Ongoing saves
 *
 * {@link QueryProvider} subscribes with `persistQueryClientSubscribe` so cache updates are written
 * back. We intentionally **do not** run a second `persistQueryClientRestore` in React: bootstrap
 * already restored; repeating restore in an effect would re-read storage for no benefit.
 */
export const QUERY_PERSIST_STORAGE_KEY = 'casino-gw:rq-cache';

/**
 * Only caches that are safe to leave on disk are listed here.
 *
 * `user-context` is deliberately excluded: `fetchUserContextApi` merges the
 * account holder's `accountID`, `username`, `firstName` and `lastName` into its
 * response, so persisting it would write customer PII to localStorage where any
 * same-origin script can read it, and it would outlive logout on a shared
 * device. The query sets `refetchOnMount: 'always'`, so it re-fetches on load
 * anyway and loses nothing but a stale first paint (CAS-67361).
 *
 * Anything added here must be checked the same way: entries are stored in the
 * clear and are not cleared on logout.
 */
const PERSISTED_QUERY_FIRST_SEGMENTS = new Set([
  'aggregation',
  'customer-data',
  'recently-played',
]);

export function shouldPersistQueryToStorage(query: Query): boolean {
  return (
    query.state.status === 'success' &&
    typeof query.queryKey[0] === 'string' &&
    PERSISTED_QUERY_FIRST_SEGMENTS.has(query.queryKey[0])
  );
}

export type AppQueryPersister = ReturnType<typeof createQueryPersister>;

export const createQueryPersister = () =>
  createAsyncStoragePersister({
    storage: window.localStorage,
    key: QUERY_PERSIST_STORAGE_KEY,
  });

/** Options for `persistQueryClientSubscribe` (and compatible with save helpers). */
export function buildPersistSubscribeOptions(
  queryClient: QueryClient,
  persister: AppQueryPersister,
): PersistQueryClientOptions {
  return {
    queryClient,
    persister,
    maxAge: QUERY_PERSIST_MAX_AGE_MS,
    dehydrateOptions: {
      shouldDehydrateQuery: shouldPersistQueryToStorage,
    },
  };
}
